// M13 dashboard widget — unread / recent / critical / system alerts at a glance.
// Self-contained and additive: safe to embed in frozen dashboards.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Bell, Megaphone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { resolveNotificationLink } from "@/lib/notificationLinks";
import { useAuth } from "@/context/AuthContext";

export const NotificationsWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/notifications/summary")
      .then((res) => setSummary(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openItem = async (n) => {
    try {
      if (n.status === "Unread") await api.put(`/notifications/${n.id}/read`);
    } catch { /* non-blocking */ }
    navigate(resolveNotificationLink(n, user.role) || "/notifications");
  };

  return (
    <Card data-testid="notifications-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" /> Notifications
          </CardTitle>
          <div className="flex items-center gap-2">
            {summary?.unread > 0 && (
              <Badge variant="secondary" className="rounded-full" data-testid="widget-unread-count">
                {summary.unread} unread
              </Badge>
            )}
            {summary?.critical_unread > 0 && (
              <Badge className="rounded-full bg-red-100 text-red-700 hover:bg-red-100" data-testid="widget-critical-count">
                <AlertTriangle className="h-3 w-3 mr-1" /> {summary.critical_unread} critical
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}
          </div>
        ) : !summary || summary.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="widget-empty-state">
            No notifications yet — business events will appear here.
          </p>
        ) : (
          <ul className="space-y-1" data-testid="widget-recent-list">
            {summary.recent.slice(0, 5).map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => openItem(n)}
                  className={`w-full text-left rounded-lg px-3 py-2 hover:bg-muted/60 transition-colors ${n.status === "Unread" ? "bg-primary/[0.04]" : ""}`}
                  data-testid={`widget-notification-${n.notify_code}`}
                >
                  <div className="flex items-center gap-2">
                    {n.notification_type === "Admin Announcement" ? (
                      <Megaphone className="h-3.5 w-3.5 text-primary shrink-0" />
                    ) : n.priority === "Critical" ? (
                      <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                    )}
                    <span className={`text-sm truncate flex-1 ${n.status === "Unread" ? "font-semibold" : "text-muted-foreground"}`}>
                      {n.title}
                    </span>
                    <span className="text-[11px] text-muted-foreground/70 shrink-0">
                      {formatDateTime(n.last_merged_at || n.created_at)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Button variant="ghost" size="sm" className="w-full mt-2 gap-1.5 text-primary"
                onClick={() => navigate("/notifications")} data-testid="widget-view-all">
          Open Notification Center <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </CardContent>
    </Card>
  );
};
